import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../../api/client'
import { userAPI } from '../../api/client'
import type { DiagnosticResult } from '@enghustle/shared'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

interface DiagnosticResultExtended extends DiagnosticResult {
  daysNeeded: number | null
  daysAvailable: number | null
  targetLevel: string
  dailyStudyMinutes: number
}

function UnrealisticGoalWarning({
  daysNeeded,
  daysAvailable,
  targetLevel,
  dailyStudyMinutes,
  onClose,
}: {
  daysNeeded: number
  daysAvailable: number
  targetLevel: string
  dailyStudyMinutes: number
  onClose: () => void
}) {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const levelOrder = ['A2', 'B1', 'B2', 'C1']
  const currentIdx = levelOrder.indexOf(targetLevel)
  const lowerTarget = currentIdx > 0 ? levelOrder[currentIdx - 1] : targetLevel

  // Calculate realistic forecast: one level below target
  const forecastLevel = lowerTarget !== targetLevel ? lowerTarget : 'A2'

  async function handleAdjustTarget() {
    setLoading(true)
    try { await userAPI.updateProfile({ targetLevel: lowerTarget as any }) } catch {}
    setLoading(false)
    onClose()
  }

  async function handleIncreaseTime() {
    setLoading(true)
    const newMinutes = Math.min(120, dailyStudyMinutes + 15)
    try { await userAPI.updateProfile({ dailyStudyMinutes: newMinutes }) } catch {}
    setLoading(false)
    onClose()
  }

  async function handleKeepAll() {
    setLoading(true)
    try {
      // Store forecast level so dashboard can show "Dự báo đạt: B1+" instead of target
      await api.patch('/users/me/forecast-level', { forecastLevel })
    } catch {}
    setLoading(false)
    onClose()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(6,14,32,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
    }}>
      <div style={{
        background: 'white', borderRadius: '2rem', padding: '2.5rem',
        maxWidth: '28rem', width: '100%', boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>⚠️</div>
          <H style={{ display: 'block', fontSize: '1.375rem', fontWeight: 800, marginBottom: '0.5rem', color: '#272e42' }}>
            {t('diagnostic.unrealisticTitle')}
          </H>
          <p style={{ color: '#535b71', fontSize: '0.9rem', lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: t('diagnostic.unrealisticDesc', { daysNeeded, daysAvailable }).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            onClick={handleAdjustTarget}
            disabled={loading || lowerTarget === targetLevel}
            style={{
              padding: '1rem 1.25rem', borderRadius: '1rem', border: '2px solid #004be2',
              background: '#eef0ff', cursor: 'pointer', textAlign: 'left',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            <H style={{ display: 'block', fontWeight: 800, color: '#004be2', marginBottom: '0.25rem' }}>
              {t('diagnostic.option1Title', { level: lowerTarget })}
            </H>
            <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>{t('diagnostic.option1Desc')}</span>
          </button>

          <button
            onClick={handleIncreaseTime}
            disabled={loading}
            style={{
              padding: '1rem 1.25rem', borderRadius: '1rem', border: '2px solid #8e00d5',
              background: '#f5e6ff', cursor: 'pointer', textAlign: 'left',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            <H style={{ display: 'block', fontWeight: 800, color: '#8e00d5', marginBottom: '0.25rem' }}>
              {t('diagnostic.option2Title')}
            </H>
            <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>
              {t('diagnostic.option2Desc', { from: dailyStudyMinutes, to: Math.min(120, dailyStudyMinutes + 15) })}
            </span>
          </button>

          <button
            onClick={handleKeepAll}
            disabled={loading}
            style={{
              padding: '1rem 1.25rem', borderRadius: '1rem', border: '2px solid #d9e2ff',
              background: 'white', cursor: 'pointer', textAlign: 'left',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            <H style={{ display: 'block', fontWeight: 800, color: '#535b71', marginBottom: '0.25rem' }}>
              {t('diagnostic.option3Title')}
            </H>
            <span style={{ fontSize: '0.8125rem', color: '#a5adc6' }}>{t('diagnostic.option3Desc')}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default function DiagnosticPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [answer, setAnswer] = useState('')
  const [result, setResult] = useState<DiagnosticResultExtended | null>(null)
  const [questionCount, setQuestionCount] = useState(0)
  const [start] = useState(Date.now())
  const [showWarning, setShowWarning] = useState(false)

  const { data: question, refetch, isLoading } = useQuery({
    queryKey: ['diagnostic-question'],
    queryFn: () => api.get<any>('/diagnostic/next-question'),
  })

  const submitAnswer = useMutation({
    mutationFn: () => api.post('/diagnostic/answer', {
      questionId: question.id,
      answer,
      timeSpentSeconds: Math.round((Date.now() - start) / 1000),
    }),
    onSuccess: (data: any) => {
      setQuestionCount(q => q + 1)
      if (data.questionsAsked >= 30 || data.se < 0.32) {
        complete.mutate()
      } else {
        setAnswer('')
        refetch()
      }
    },
  })

  const complete = useMutation({
    mutationFn: () => api.post<DiagnosticResultExtended>('/diagnostic/complete'),
    onSuccess: (data) => {
      setResult(data)
      // Show warning if examDate exists and days available < 70% of days needed
      if (
        data.daysNeeded != null &&
        data.daysAvailable != null &&
        data.daysAvailable < data.daysNeeded * 0.7
      ) {
        setShowWarning(true)
      }
    },
  })

  // When backend has no more questions (DB empty or all answered), auto-complete
  useEffect(() => {
    if (question?.done && !result && !complete.isPending) {
      complete.mutate()
    }
  }, [question])

  if (result && !showWarning) return <ResultView result={result} onContinue={() => navigate('/roadmap')} />

  const estimatedTotal = 20
  const progress = Math.min((questionCount / estimatedTotal) * 100, 95)

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f6ff', fontFamily: "'Manrope',sans-serif", padding: '2rem', position: 'relative', overflow: 'hidden' }}>
      {/* Warning modal */}
      {showWarning && result && (
        <UnrealisticGoalWarning
          daysNeeded={result.daysNeeded!}
          daysAvailable={result.daysAvailable!}
          targetLevel={result.targetLevel}
          dailyStudyMinutes={result.dailyStudyMinutes}
          onClose={() => setShowWarning(false)}
        />
      )}
      {/* Blobs */}
      <div style={{ position: 'fixed', top: '-8rem', right: '-8rem', width: '28rem', height: '28rem', borderRadius: '50%', background: 'rgba(0,75,226,0.05)', filter: 'blur(80px)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '-8rem', left: '-8rem', width: '28rem', height: '28rem', borderRadius: '50%', background: 'rgba(142,0,213,0.05)', filter: 'blur(80px)', pointerEvents: 'none' }} />

      <div style={{ width: '100%', maxWidth: '40rem', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 1rem', borderRadius: '9999px', background: 'rgba(0,75,226,0.08)', marginBottom: '1.5rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>psychology</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>{t('diagnostic.badge')}</span>
          </div>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>{t('diagnostic.title')}</H>
          <p style={{ color: '#535b71', fontSize: '0.9375rem' }}>{t('diagnostic.subtitle')}</p>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#535b71' }}>{t('diagnostic.question')} {questionCount + 1}</span>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#004be2' }}>{Math.round(progress)}{t('diagnostic.complete')}</span>
          </div>
          <div style={{ height: '6px', borderRadius: '9999px', background: '#d9e2ff', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#004be2,#809bff)', borderRadius: '9999px', transition: 'width 0.5s ease' }} />
          </div>
        </div>

        {isLoading || !question || question.done ? (
          <div style={{ background: 'white', borderRadius: '2rem', padding: '4rem', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.04)' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }} />
            <p style={{ color: '#535b71', fontWeight: 600 }}>{t('diagnostic.processing')}</p>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '2rem', padding: '2.5rem', boxShadow: '0 20px 40px rgba(0,0,0,0.04)' }}>
            {/* Question number */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem' }}>
              <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, flexShrink: 0 }}>
                {questionCount + 1}
              </div>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#535b71' }}>
                {question.skill ?? 'Reading'} · {question.questionType ?? 'Multiple Choice'}
              </span>
            </div>

            {/* Prompt */}
            <p style={{ fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.6, marginBottom: '2rem', color: '#272e42' }}>
              {question.prompt}
            </p>

            {/* Options or text input */}
            {question.options ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                {Object.entries(question.options).map(([label, opt]) => {
                  const sel = answer === opt
                  return (
                    <label
                      key={label}
                      onClick={() => setAnswer(opt as string)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem', borderRadius: '1rem', cursor: 'pointer', transition: 'all 0.2s',
                        background: sel ? 'rgba(0,75,226,0.06)' : '#eef0ff',
                        boxShadow: sel ? '0 0 0 2px #004be2' : 'none',
                      }}
                    >
                      <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0, background: sel ? '#004be2' : '#d9e2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sel ? 'white' : '#535b71', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '0.8125rem', transition: 'all 0.2s' }}>
                        {label}
                      </div>
                      <span style={{ fontSize: '0.9375rem', fontWeight: sel ? 700 : 500, color: sel ? '#272e42' : '#535b71', transition: 'color 0.2s' }}>{opt as string}</span>
                    </label>
                  )
                })}
              </div>
            ) : question.questionType === 'multiple_choice' ? (
              <div style={{ padding: '1rem 1.25rem', borderRadius: '0.75rem', background: 'rgba(180,19,64,0.06)', color: '#b41340', fontSize: '0.875rem', fontWeight: 600, marginBottom: '2rem' }}>
                {t('diagnostic.questionDataError', 'Câu hỏi này bị thiếu dữ liệu đáp án. Vui lòng bấm "Trả lời" để bỏ qua.')}
              </div>
            ) : (
              <textarea
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                placeholder={t('diagnostic.answerPlaceholder')}
                rows={4}
                style={{ width: '100%', padding: '1rem 1.25rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope',sans-serif", fontSize: '1rem', fontWeight: 500, color: '#272e42', resize: 'vertical', marginBottom: '2rem', transition: 'all 0.2s' }}
                onFocus={e => { e.target.style.background = '#f6f6ff'; e.target.style.boxShadow = '0 0 0 2px #004be2' }}
                onBlur={e => { e.target.style.background = '#eef0ff'; e.target.style.boxShadow = 'none' }}
              />
            )}

            {submitAnswer.isError && (
              <div style={{ padding: '0.875rem', borderRadius: '0.75rem', background: 'rgba(180,19,64,0.08)', color: '#b41340', fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem' }}>
                {(submitAnswer.error as Error).message}
              </div>
            )}

            <button
              onClick={() => submitAnswer.mutate()}
              disabled={!answer || submitAnswer.isPending}
              style={{
                width: '100%', padding: '1.125rem', borderRadius: '9999px', border: 'none', cursor: (!answer || submitAnswer.isPending) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                background: (!answer || submitAnswer.isPending) ? 'rgba(0,75,226,0.35)' : 'linear-gradient(135deg,#004be2,#809bff)',
                color: '#f2f1ff', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1rem',
                boxShadow: '0 8px 24px rgba(0,75,226,0.15)', transition: 'all 0.2s',
              }}
            >
              {submitAnswer.isPending ? (
                <><span className="spinner" style={{ width: '1.25rem', height: '1.25rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> {t('diagnostic.processing')}</>
              ) : (
                <>{t('diagnostic.answer')} <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>arrow_forward</span></>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function ResultView({ result, onContinue }: { result: DiagnosticResult; onContinue: () => void }) {
  const { t } = useTranslation()
  const skillIcons: Record<string, string> = {
    listening: 'headphones', reading: 'menu_book', writing: 'edit_note', speaking: 'mic',
  }
  const skillColors: Record<string, string> = {
    listening: '#006571', reading: '#004be2', writing: '#8e00d5', speaking: '#b41340',
  }
  const skillBg: Record<string, string> = {
    listening: '#e6fafb', reading: '#e8eeff', writing: '#f5e6ff', speaking: '#ffe8ed',
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f6f6ff', fontFamily: "'Manrope',sans-serif", padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: '42rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ width: '5rem', height: '5rem', borderRadius: '50%', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 12px 32px rgba(0,75,226,0.3)' }}>
            <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '2.5rem', fontVariationSettings: "'FILL' 1" }}>verified</span>
          </div>
          <H style={{ display: 'block', fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>{t('diagnostic.resultTitle')}</H>
          <p style={{ color: '#535b71' }}>{t('diagnostic.resultSubtitle')}</p>
        </div>

        {/* Level card */}
        <div style={{ background: 'linear-gradient(135deg,#004be2,#809bff)', borderRadius: '2rem', padding: '2.5rem', color: 'white', textAlign: 'center', marginBottom: '1.5rem', boxShadow: '0 20px 48px rgba(0,75,226,0.25)' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.7, display: 'block', marginBottom: '0.75rem' }}>{t('diagnostic.overallLevel')}</span>
          <H style={{ display: 'block', fontSize: '4rem', fontWeight: 900, letterSpacing: '-0.04em', marginBottom: '0.5rem' }}>{result.overallLevel}</H>
          <p style={{ opacity: 0.8, marginBottom: '1.5rem' }}>
            {t('diagnostic.recommendedTarget')} <strong>{result.recommendedTarget}</strong> · {result.recommendedDaysNeeded} {t('diagnostic.days')}
          </p>
        </div>

        {/* Skills grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {Object.entries(result.skillBreakdown).map(([skill, data]) => {
            const icon = skillIcons[skill] ?? 'star'
            const color = skillColors[skill] ?? '#004be2'
            const bg = skillBg[skill] ?? '#eef0ff'
            const score = data.score ?? 0
            return (
              <div key={skill} style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="material-symbols-outlined" style={{ color, fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>{icon}</span>
                  </div>
                  <div>
                    <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, textTransform: 'capitalize', color: '#272e42' }}>{skill}</H>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color }}>{data.level}</span>
                  </div>
                </div>
                <div style={{ height: '6px', borderRadius: '9999px', background: '#d9e2ff', overflow: 'hidden', marginBottom: '0.5rem' }}>
                  <div style={{ height: '100%', width: `${Math.min(score * 10, 100)}%`, background: `linear-gradient(90deg,${color},${color}99)`, borderRadius: '9999px' }} />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#535b71' }}>{score.toFixed(1)}/10</span>
                {data.weaknesses?.length > 0 && (
                  <p style={{ fontSize: '0.6875rem', color: '#b41340', marginTop: '0.375rem', fontWeight: 600 }}>{t('diagnostic.needImprove')} {data.weaknesses.slice(0, 2).join(', ')}</p>
                )}
              </div>
            )
          })}
        </div>

        <button
          onClick={onContinue}
          style={{
            width: '100%', padding: '1.25rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
            background: 'linear-gradient(135deg,#004be2,#809bff)', color: '#f2f1ff',
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1.0625rem',
            boxShadow: '0 8px 24px rgba(0,75,226,0.2)', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,75,226,0.35)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,75,226,0.2)' }}
        >
          {t('diagnostic.viewRoadmap')}
          <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>arrow_forward</span>
        </button>
      </div>
    </div>
  )
}
